import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

/* ─── TOKENS ─────────────────────────────────────────────────────────── */
const T = {
  bg:      '#07080c',
  bg2:     '#0c0e14',
  bg3:     '#111520',
  surface: '#161b26',
  surf2:   '#1c2233',
  border:  'rgba(255,255,255,.06)',
  borderHi:'rgba(255,255,255,.11)',
  white:   '#dde5f5',
  muted:   'rgba(255,255,255,0.50)',
  dim:     'rgba(255,255,255,0.50)',
  blue:    '#3d8eff',
  cyan:    '#00c8f0',
  green:   '#2ecc71',
  red:     '#e74c3c',
  orange:  '#f39c12',
  purple:  '#9b59b6',
  sans:    '"Syne","DM Sans",system-ui,sans-serif',
  mono:    '"JetBrains Mono","Fira Code",monospace',
};

/* ─── SHARED ATOMS ───────────────────────────────────────────────────── */
function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, marginBottom:0 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={()=>onChange(t.id)} style={{
          padding:'12px 20px',
          border:'none',
          borderBottom:`2px solid ${active===t.id ? T.cyan : 'transparent'}`,
          background:'transparent',
          color: active===t.id ? T.white : T.muted,
          fontSize:13, fontWeight: active===t.id ? 700 : 400,
          fontFamily:T.sans, cursor:'pointer',
          display:'flex', alignItems:'center', gap:8,
          marginBottom:-1, transition:'color .15s',
          letterSpacing:'.01em',
        }}>
          <span style={{
            fontSize:16, filter: active===t.id ? 'none' : 'grayscale(1) opacity(.5)',
          }}>{t.icon}</span>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function DropZone({ onFiles, accept, multiple=true, children, compact=false }) {
  const [drag, setDrag] = useState(false);
  const ref = useRef();

  const handle = (files) => {
    const arr = Array.from(files).filter(f =>
      accept ? accept.some(a => f.type.includes(a) || f.name.endsWith(a)) : true
    );
    if (arr.length) onFiles(arr);
  };

  return (
    <div
      onClick={() => ref.current.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      style={{
        border:`1.5px dashed ${drag ? T.cyan : T.borderHi}`,
        borderRadius:12,
        padding: compact ? '18px' : '36px 24px',
        textAlign:'center',
        cursor:'pointer',
        background: drag ? `rgba(0,200,240,.05)` : T.bg2,
        transition:'all .18s',
        userSelect:'none',
      }}
    >
      <input
        ref={ref} type="file"
        accept={accept?.join(',')}
        multiple={multiple}
        style={{ display:'none' }}
        onChange={e => handle(e.target.files)}
      />
      {children}
    </div>
  );
}

function Btn({ onClick, disabled, loading, children, variant='primary', small }) {
  const colors = {
    primary: { bg:T.blue+'22', border:T.blue+'55', color:T.blue },
    success: { bg:T.green+'18', border:T.green+'50', color:T.green },
    danger:  { bg:T.red+'14',  border:T.red+'45',   color:T.red  },
    ghost:   { bg:'transparent', border:T.border,  color:T.muted },
  };
  const c = colors[variant];
  return (
    <button onClick={onClick} disabled={disabled||loading} style={{
      padding: small ? '5px 12px' : '8px 18px',
      borderRadius:7, border:`1px solid ${c.border}`,
      background:c.bg, color:c.color,
      fontSize: small ? 11.5 : 13,
      fontWeight:600, fontFamily:T.sans,
      cursor: disabled||loading ? 'default':'pointer',
      opacity: disabled||loading ? .45 : 1,
      display:'inline-flex', alignItems:'center', gap:6,
      transition:'all .14s', whiteSpace:'nowrap',
    }}>
      {loading && <Spin size={11}/>}
      {children}
    </button>
  );
}

function Spin({ size=14 }) {
  return (
    <div style={{
      width:size, height:size, borderRadius:'50%',
      border:`2px solid rgba(255,255,255,.12)`,
      borderTopColor:'currentColor',
      animation:'spin .65s linear infinite', flexShrink:0,
    }}/>
  );
}

function Tag({ children, color=T.blue }) {
  return (
    <span style={{
      padding:'2px 7px', borderRadius:4,
      background:color+'18', border:`1px solid ${color}35`,
      color, fontSize:10.5, fontWeight:700,
      fontFamily:T.mono, letterSpacing:'.04em',
      textTransform:'uppercase',
    }}>{children}</span>
  );
}

function Progress({ value, label, color=T.cyan }) {
  return (
    <div style={{ marginTop:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
        <span style={{ fontSize:11.5, color:T.muted }}>{label}</span>
        <span style={{ fontSize:11.5, color, fontFamily:T.mono }}>{Math.round(value)}%</span>
      </div>
      <div style={{ height:4, background:T.border, borderRadius:2, overflow:'hidden' }}>
        <div style={{
          height:'100%', width:`${value}%`,
          background:`linear-gradient(90deg,${color},${color}aa)`,
          borderRadius:2, transition:'width .3s ease',
        }}/>
      </div>
    </div>
  );
}

function FileRow({ file, meta, onRemove, extra }) {
  const kb = (file.size/1024).toFixed(1);
  const mb = (file.size/1024/1024).toFixed(2);
  const size = file.size > 1024*1024 ? `${mb} MB` : `${kb} KB`;
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:10,
      padding:'8px 10px',
      background:T.bg2, border:`1px solid ${T.border}`,
      borderRadius:8,
    }}>
      <div style={{
        width:36, height:36, borderRadius:7, flexShrink:0,
        background:T.surface, border:`1px solid ${T.borderHi}`,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:18,
      }}>
        {meta?.preview
          ? <img src={meta.preview} alt="" style={{ width:34, height:34, objectFit:'cover', borderRadius:6 }}/>
          : meta?.icon || '📄'}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{
          fontSize:12.5, fontWeight:600, color:T.white,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>{file.name}</div>
        <div style={{ fontSize:11, color:T.dim, marginTop:1 }}>{size}</div>
      </div>
      {extra}
      {onRemove && (
        <button onClick={onRemove} style={{
          width:22, height:22, borderRadius:5, border:`1px solid ${T.border}`,
          background:'transparent', color:T.muted, cursor:'pointer', flexShrink:0,
          fontSize:12, fontFamily:'inherit',
        }}>✕</button>
      )}
    </div>
  );
}

function SectionHead({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
        <span style={{ fontSize:22 }}>{icon}</span>
        <h2 style={{
          fontSize:18, fontWeight:800, color:T.white,
          fontFamily:T.sans, letterSpacing:'-.01em',
        }}>{title}</h2>
      </div>
      <p style={{ fontSize:13, color:T.muted, marginLeft:32 }}>{subtitle}</p>
    </div>
  );
}

/* ─── TOOL 1: IMAGE CONVERTER ────────────────────────────────────────── */
const IMG_FORMATS = [
  { id:'jpeg', label:'JPG', mime:'image/jpeg', ext:'jpg' },
  { id:'png',  label:'PNG', mime:'image/png',  ext:'png' },
  { id:'webp', label:'WebP',mime:'image/webp', ext:'webp'},
  { id:'bmp',  label:'BMP', mime:'image/bmp',  ext:'bmp' },
  { id:'gif',  label:'GIF', mime:'image/gif',  ext:'gif' },
];

function ImageConverter() {
  const [files,    setFiles]    = useState([]);
  const [targetFmt,setTargetFmt]= useState('webp');
  const [quality,  setQuality]  = useState(90);
  const [results,  setResults]  = useState([]);
  const [converting,setConverting]=useState(false);
  const [progress, setProgress] = useState(0);

  const addFiles = useCallback((newFiles) => {
    const processed = newFiles.map(f => ({
      file: f,
      id:   Math.random().toString(36).slice(2),
      preview: URL.createObjectURL(f),
      ext: f.name.split('.').pop().toLowerCase(),
    }));
    setFiles(prev => [...prev, ...processed]);
    setResults([]);
  }, []);

  const removeFile = (id) => {
    setFiles(prev => {
      const item = prev.find(f=>f.id===id);
      if (item) URL.revokeObjectURL(item.preview);
      return prev.filter(f=>f.id!==id);
    });
  };

  const convertAll = async () => {
    if (!files.length) return;
    setConverting(true);
    setResults([]);
    const fmt = IMG_FORMATS.find(f=>f.id===targetFmt);
    const out  = [];

    for (let i=0; i<files.length; i++) {
      const item = files[i];
      setProgress(Math.round((i/files.length)*100));

      try {
        const url = await convertImage(item.file, fmt.mime, quality/100);
        const origName = item.file.name.replace(/\.[^.]+$/, '');
        out.push({
          id:       item.id,
          name:     `${origName}.${fmt.ext}`,
          url,
          origSize: item.file.size,
          preview:  url,
        });
      } catch(e) {
        out.push({ id:item.id, name:item.file.name, error:e.message });
      }
    }

    setProgress(100);
    setResults(out);
    setConverting(false);
  };

  const convertImage = (file, targetMime, quality) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const src = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        // White background for JPG (no transparency)
        if (targetMime === 'image/jpeg') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(src);
        const dataUrl = canvas.toDataURL(targetMime, quality);
        resolve(dataUrl);
      };
      img.onerror = () => { URL.revokeObjectURL(src); reject(new Error('Could not load image')); };
      img.src = src;
    });
  };

  const downloadAll = () => {
    results.filter(r=>!r.error).forEach(r => {
      const a = document.createElement('a');
      a.href = r.url;
      a.download = r.name;
      a.click();
    });
  };

  const downloadOne = (r) => {
    const a = document.createElement('a');
    a.href = r.url;
    a.download = r.name;
    a.click();
  };

  const fmt = IMG_FORMATS.find(f=>f.id===targetFmt);
  const hasJpg = targetFmt === 'jpeg';

  return (
    <div>
      <SectionHead
        icon="🖼️"
        title="Image Converter"
        subtitle="Convert between PNG, JPG, WebP, BMP, GIF — all in your browser, no upload"
      />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Left — Upload */}
        <div>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:T.dim, marginBottom:8 }}>
            Input Images
          </div>
          <DropZone
            onFiles={addFiles}
            accept={['image/']}
            multiple
          >
            <div style={{ fontSize:28, marginBottom:8 }}>🖼️</div>
            <div style={{ fontSize:13.5, fontWeight:600, color:T.white, marginBottom:4 }}>
              Drop images here or click to browse
            </div>
            <div style={{ fontSize:11.5, color:T.dim }}>
              PNG · JPG · WebP · BMP · GIF · SVG
            </div>
          </DropZone>

          {files.length > 0 && (
            <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:6 }}>
              {files.map(item => (
                <FileRow
                  key={item.id}
                  file={item.file}
                  meta={{ preview: item.preview }}
                  onRemove={() => removeFile(item.id)}
                  extra={<Tag color={T.cyan}>{item.ext.toUpperCase()}</Tag>}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right — Settings */}
        <div>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:T.dim, marginBottom:8 }}>
            Output Format
          </div>

          {/* Format pills */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
            {IMG_FORMATS.map(f => (
              <button key={f.id} onClick={()=>setTargetFmt(f.id)} style={{
                padding:'7px 14px',
                borderRadius:7,
                border:`1px solid ${targetFmt===f.id ? T.cyan+'60' : T.border}`,
                background: targetFmt===f.id ? T.cyan+'16' : T.bg2,
                color: targetFmt===f.id ? T.cyan : T.muted,
                fontSize:12.5, fontWeight:700,
                fontFamily:T.mono, cursor:'pointer',
                transition:'all .13s',
              }}>{f.label}</button>
            ))}
          </div>

          {/* Quality slider — only for lossy formats */}
          {(targetFmt === 'jpeg' || targetFmt === 'webp') && (
            <div style={{ marginBottom:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <span style={{ fontSize:11.5, color:T.muted }}>Quality</span>
                <span style={{ fontSize:12, color:T.cyan, fontFamily:T.mono, fontWeight:700 }}>{quality}%</span>
              </div>
              <input
                type="range" min={10} max={100} value={quality}
                onChange={e=>setQuality(Number(e.target.value))}
                style={{ width:'100%', accentColor:T.cyan }}
              />
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:T.dim, marginTop:2 }}>
                <span>Smaller file</span>
                <span>Better quality</span>
              </div>
            </div>
          )}

          {/* Format info */}
          <div style={{
            padding:'10px 12px', borderRadius:8,
            background:T.surface, border:`1px solid ${T.border}`,
            fontSize:12, color:T.muted, lineHeight:1.65, marginBottom:14,
          }}>
            {targetFmt==='jpeg' && '📷 JPG — Best for photos. Smaller files, lossy compression. No transparency.'}
            {targetFmt==='png'  && '🎨 PNG — Lossless quality. Supports transparency. Best for graphics/UI.'}
            {targetFmt==='webp' && '⚡ WebP — Modern format. Best compression with great quality. Transparent support.'}
            {targetFmt==='bmp'  && '🗂 BMP — Uncompressed bitmap. Large files, maximum compatibility.'}
            {targetFmt==='gif'  && '🎞 GIF — Limited to 256 colors. Good for simple graphics, supports animation.'}
          </div>

          {/* Convert button */}
          <Btn
            onClick={convertAll}
            disabled={!files.length}
            loading={converting}
            variant="primary"
          >
            Convert {files.length > 0 ? `${files.length} image${files.length>1?'s':''}` : 'images'} → {fmt?.label}
          </Btn>

          {converting && <Progress value={progress} label="Converting…"/>}
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div style={{ marginTop:20 }}>
          <div style={{
            display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10,
          }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.white }}>
              ✅ {results.filter(r=>!r.error).length} converted
            </div>
            {results.filter(r=>!r.error).length > 1 && (
              <Btn onClick={downloadAll} variant="success" small>
                ⬇ Download All
              </Btn>
            )}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {results.map(r => (
              <div key={r.id} style={{
                display:'flex', alignItems:'center', gap:10,
                padding:'8px 10px',
                background:T.bg2, border:`1px solid ${r.error ? T.red+'35' : T.green+'25'}`,
                borderRadius:8,
              }}>
                {r.error ? (
                  <>
                    <span style={{ fontSize:14 }}>❌</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12.5, color:T.red }}>{r.name}</div>
                      <div style={{ fontSize:11, color:T.red+'90' }}>{r.error}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <img src={r.preview} alt="" style={{
                      width:36, height:36, objectFit:'cover',
                      borderRadius:6, border:`1px solid ${T.borderHi}`,
                    }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{
                        fontSize:12.5, fontWeight:600, color:T.white,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                      }}>{r.name}</div>
                    </div>
                    <Tag color={T.green}>{fmt?.label}</Tag>
                    <Btn onClick={()=>downloadOne(r)} variant="success" small>
                      ⬇ Download
                    </Btn>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── TOOL 2: PDF MERGER ─────────────────────────────────────────────── */
function PDFMerger() {
  const [files,    setFiles]    = useState([]);
  const [merging,  setMerging]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState('');
  const [progress, setProgress] = useState(0);
  const dragItem   = useRef(null);
  const dragOver   = useRef(null);
const API = process.env.REACT_APP_API_URL || 'https://dev.arhat.info';

  const addFiles = useCallback((newFiles) => {
    const pdfs = newFiles.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    setFiles(prev => [...prev, ...pdfs.map(f => ({
      file: f, id: Math.random().toString(36).slice(2),
    }))]);
    setResult(null); setError('');
  }, []);

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f=>f.id!==id));
    setResult(null);
  };

  // Drag to reorder
  const onDragStart  = (i) => { dragItem.current = i; };
  const onDragEnter  = (i) => { dragOver.current = i; };
  const onDragEnd    = () => {
    const copy = [...files];
    const dragged = copy.splice(dragItem.current, 1)[0];
    copy.splice(dragOver.current, 0, dragged);
    dragItem.current = null; dragOver.current = null;
    setFiles(copy);
  };

  const merge = async () => {
    if (files.length < 2) { setError('Add at least 2 PDF files to merge.'); return; }
    setMerging(true); setError(''); setResult(null); setProgress(0);

    try {
      const formData = new FormData();
      files.forEach((item, i) => {
        formData.append(`file_${i}`, item.file);
        formData.append(`order_${i}`, i);
      });

      setProgress(20);

      const res = await fetch(`${API}/api/tools/pdf/merge/`, {
        method: 'POST',
        body:   formData,
      });

      setProgress(80);

      if (!res.ok) {
        const err = await res.json().catch(()=>({error:'Merge failed'}));
        throw new Error(err.error || `Server error ${res.status}`);
      }

      const blob  = await res.blob();
      const url   = URL.createObjectURL(blob);
      const total = files.reduce((s,f)=>s+f.file.size,0);

      setProgress(100);
      setResult({
        url,
        name:     'merged.pdf',
        size:     blob.size,
        origSize: total,
        pages:    files.length,
      });
    } catch(e) {
      setError(e.message);
    } finally {
      setMerging(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = result.name;
    a.click();
  };

  const totalSize = files.reduce((s,f)=>s+f.file.size,0);
  const fmtSize   = (b) => b > 1024*1024 ? `${(b/1024/1024).toFixed(1)} MB` : `${(b/1024).toFixed(0)} KB`;

  return (
    <div>
      <SectionHead
        icon="📑"
        title="PDF Merger"
        subtitle="Combine multiple PDFs into one. Drag to reorder before merging."
      />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:16 }}>
        {/* File list */}
        <div>
          <DropZone onFiles={addFiles} accept={['.pdf','application/pdf']} compact>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
              <span style={{ fontSize:22 }}>📄</span>
              <div style={{ textAlign:'left' }}>
                <div style={{ fontSize:13, fontWeight:600, color:T.white }}>Drop PDFs here or click to browse</div>
                <div style={{ fontSize:11, color:T.dim }}>Add multiple PDF files</div>
              </div>
            </div>
          </DropZone>

          {files.length > 0 && (
            <div style={{ marginTop:12 }}>
              <div style={{
                display:'flex', justifyContent:'space-between',
                alignItems:'center', marginBottom:8,
              }}>
                <span style={{ fontSize:11, color:T.dim }}>
                  {files.length} file{files.length>1?'s':''} · {fmtSize(totalSize)} total · drag to reorder
                </span>
                <Btn
                  onClick={()=>{ setFiles([]); setResult(null); setError(''); }}
                  variant="ghost" small
                >
                  Clear all
                </Btn>
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                {files.map((item, i) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragEnter={() => onDragEnter(i)}
                    onDragEnd={onDragEnd}
                    style={{
                      display:'flex', alignItems:'center', gap:8,
                      padding:'8px 10px',
                      background:T.bg2, border:`1px solid ${T.border}`,
                      borderRadius:8, cursor:'grab',
                    }}
                  >
                    {/* order badge */}
                    <div style={{
                      width:22, height:22, borderRadius:5, flexShrink:0,
                      background:T.surface, border:`1px solid ${T.borderHi}`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:11, fontWeight:700, color:T.cyan, fontFamily:T.mono,
                    }}>{i+1}</div>
                    <span style={{ color:T.dim, fontSize:14, flexShrink:0 }}>⠿</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{
                        fontSize:12.5, fontWeight:600, color:T.white,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                      }}>{item.file.name}</div>
                      <div style={{ fontSize:11, color:T.dim }}>{fmtSize(item.file.size)}</div>
                    </div>
                    <button onClick={()=>removeFile(item.id)} style={{
                      width:20, height:20, borderRadius:4, border:`1px solid ${T.border}`,
                      background:'transparent', color:T.muted, cursor:'pointer', fontSize:11,
                    }}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div>
          <div style={{
            padding:'16px', background:T.bg2,
            border:`1px solid ${T.border}`, borderRadius:10,
            marginBottom:12,
          }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.white, marginBottom:10 }}>
              Merge Summary
            </div>
            {[
              ['Files to merge', files.length || '—'],
              ['Total size',     files.length ? fmtSize(totalSize) : '—'],
            ].map(([label,val]) => (
              <div key={label} style={{
                display:'flex', justifyContent:'space-between',
                padding:'5px 0', borderBottom:`1px solid ${T.border}`,
                fontSize:12,
              }}>
                <span style={{ color:T.dim }}>{label}</span>
                <span style={{ color:T.white, fontFamily:T.mono }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop:12 }}>
              <Btn
                onClick={merge}
                disabled={files.length < 2}
                loading={merging}
                variant="primary"
              >
                Merge {files.length > 0 ? `${files.length} PDFs` : 'PDFs'}
              </Btn>
            </div>
            {merging && <Progress value={progress} label="Merging…" color={T.blue}/>}
          </div>

          {error && (
            <div style={{
              padding:'10px 12px', borderRadius:8,
              background:T.red+'12', border:`1px solid ${T.red}35`,
              fontSize:12, color:T.red,
            }}>{error}</div>
          )}

          {result && (
            <div style={{
              padding:'14px', background:T.green+'10',
              border:`1px solid ${T.green}35`, borderRadius:10,
            }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.green, marginBottom:8 }}>
                ✅ Merged successfully!
              </div>
              {[
                ['Output size', fmtSize(result.size)],
                ['Input size',  fmtSize(result.origSize)],
                ['Saved',       `${Math.max(0,Math.round((1-result.size/result.origSize)*100))}%`],
              ].map(([label,val]) => (
                <div key={label} style={{
                  display:'flex', justifyContent:'space-between',
                  padding:'4px 0', fontSize:12,
                }}>
                  <span style={{ color:T.muted }}>{label}</span>
                  <span style={{ color:T.white, fontFamily:T.mono }}>{val}</span>
                </div>
              ))}
              <div style={{ marginTop:10 }}>
                <Btn onClick={download} variant="success">
                  ⬇ Download merged.pdf
                </Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── TOOL 3: PDF MINIFIER ───────────────────────────────────────────── */
function PDFMinifier() {
  const [file,     setFile]     = useState(null);
  const [level,    setLevel]    = useState('medium');
  const [minifying,setMinifying]= useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState('');
  const [progress, setProgress] = useState(0);
const API = process.env.REACT_APP_API_URL || 'https://dev.arhat.info';

  const LEVELS = [
    { id:'low',    label:'Light',    desc:'Minimal compression, fastest, best quality' },
    { id:'medium', label:'Balanced', desc:'Good compression, preserves most quality'   },
    { id:'high',   label:'Aggressive',desc:'Maximum compression, smallest file size'   },
  ];

  const addFile = useCallback((files) => {
    const pdf = files[0];
    setFile(pdf);
    setResult(null);
    setError('');
  }, []);

  const minify = async () => {
    if (!file) return;
    setMinifying(true); setError(''); setResult(null); setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file',  file);
      formData.append('level', level);

      setProgress(15);

      const res = await fetch(`${API}/api/tools/pdf/minify/`, {
        method: 'POST',
        body:   formData,
      });

      setProgress(80);

      if (!res.ok) {
        const err = await res.json().catch(()=>({error:'Compression failed'}));
        throw new Error(err.error || `Server error ${res.status}`);
      }

      const blob    = await res.blob();
      const url     = URL.createObjectURL(blob);
      const saved   = file.size - blob.size;
      const savedPct= Math.round((saved/file.size)*100);

      setProgress(100);
      setResult({
        url,
        name:     file.name.replace('.pdf', '_compressed.pdf'),
        origSize: file.size,
        newSize:  blob.size,
        saved,
        savedPct: Math.max(0, savedPct),
      });
    } catch(e) {
      setError(e.message);
    } finally {
      setMinifying(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = result.name;
    a.click();
  };

  const fmtSize = (b) => b > 1024*1024 ? `${(b/1024/1024).toFixed(2)} MB` : `${(b/1024).toFixed(0)} KB`;

  return (
    <div>
      <SectionHead
        icon="🗜️"
        title="PDF Compressor"
        subtitle="Reduce PDF file size while preserving readability. Powered by pikepdf on the server."
      />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:16 }}>
        {/* Upload */}
        <div>
          <DropZone onFiles={addFile} accept={['.pdf','application/pdf']} multiple={false}>
            <div style={{ fontSize:32, marginBottom:8 }}>🗜️</div>
            <div style={{ fontSize:13.5, fontWeight:600, color:T.white, marginBottom:4 }}>
              Drop your PDF here or click to browse
            </div>
            <div style={{ fontSize:11.5, color:T.dim }}>One PDF at a time</div>
          </DropZone>

          {file && (
            <div style={{ marginTop:10 }}>
              <FileRow
                file={file}
                meta={{ icon:'📄' }}
                onRemove={()=>{ setFile(null); setResult(null); setError(''); }}
                extra={<Tag color={T.red}>PDF</Tag>}
              />
            </div>
          )}

          {/* Compression levels */}
          <div style={{ marginTop:16 }}>
            <div style={{
              fontSize:11, fontWeight:700, letterSpacing:'.08em',
              textTransform:'uppercase', color:T.dim, marginBottom:10,
            }}>
              Compression Level
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {LEVELS.map(l => (
                <button key={l.id} onClick={()=>setLevel(l.id)} style={{
                  padding:'10px 14px',
                  borderRadius:8,
                  border:`1px solid ${level===l.id ? T.cyan+'55' : T.border}`,
                  background: level===l.id ? T.cyan+'10' : T.bg2,
                  cursor:'pointer', textAlign:'left',
                  transition:'all .13s',
                  display:'flex', alignItems:'center', gap:10,
                }}>
                  <div style={{
                    width:10, height:10, borderRadius:'50%', flexShrink:0,
                    background: level===l.id ? T.cyan : T.border,
                    boxShadow: level===l.id ? `0 0 8px ${T.cyan}` : 'none',
                  }}/>
                  <div>
                    <div style={{ fontSize:12.5, fontWeight:600, color: level===l.id ? T.white : T.muted }}>
                      {l.label}
                    </div>
                    <div style={{ fontSize:11, color:T.dim, marginTop:1 }}>{l.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Controls + Result */}
        <div>
          <div style={{
            padding:'16px', background:T.bg2,
            border:`1px solid ${T.border}`, borderRadius:10, marginBottom:12,
          }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.white, marginBottom:10 }}>
              Compression Info
            </div>
            {[
              ['File',  file ? file.name.slice(0,24)+(file.name.length>24?'…':'') : '—'],
              ['Size',  file ? fmtSize(file.size) : '—'],
              ['Level', LEVELS.find(l=>l.id===level)?.label || '—'],
            ].map(([label,val]) => (
              <div key={label} style={{
                display:'flex', justifyContent:'space-between',
                padding:'5px 0', borderBottom:`1px solid ${T.border}`, fontSize:12,
              }}>
                <span style={{ color:T.dim }}>{label}</span>
                <span style={{ color:T.white, fontFamily:T.mono, overflow:'hidden', textOverflow:'ellipsis', maxWidth:160 }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop:12 }}>
              <Btn
                onClick={minify}
                disabled={!file}
                loading={minifying}
                variant="primary"
              >
                Compress PDF
              </Btn>
            </div>
            {minifying && <Progress value={progress} label="Compressing…" color={T.orange}/>}
          </div>

          {error && (
            <div style={{
              padding:'10px 12px', borderRadius:8,
              background:T.red+'12', border:`1px solid ${T.red}35`,
              fontSize:12, color:T.red, marginBottom:10,
            }}>{error}</div>
          )}

          {result && (
            <div style={{
              padding:'14px', background:T.green+'0d',
              border:`1px solid ${T.green}35`, borderRadius:10,
            }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.green, marginBottom:10 }}>
                ✅ Compressed!
              </div>

              {/* Size comparison bar */}
              <div style={{ marginBottom:10 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:T.dim, marginBottom:4 }}>
                  <span>Before: {fmtSize(result.origSize)}</span>
                  <span>After: {fmtSize(result.newSize)}</span>
                </div>
                <div style={{ height:6, background:T.border, borderRadius:3, overflow:'hidden' }}>
                  <div style={{
                    height:'100%',
                    width:`${Math.max(5, 100-result.savedPct)}%`,
                    background:`linear-gradient(90deg,${T.green},${T.cyan})`,
                    borderRadius:3,
                  }}/>
                </div>
              </div>

              {[
                ['Original',  fmtSize(result.origSize)],
                ['Compressed',fmtSize(result.newSize)],
                ['Saved',     `${fmtSize(result.saved)} (${result.savedPct}%)`],
              ].map(([label,val]) => (
                <div key={label} style={{
                  display:'flex', justifyContent:'space-between',
                  padding:'4px 0', fontSize:12,
                }}>
                  <span style={{ color:T.muted }}>{label}</span>
                  <span style={{
                    color: label==='Saved' ? T.green : T.white,
                    fontFamily:T.mono, fontWeight: label==='Saved'?700:400,
                  }}>{val}</span>
                </div>
              ))}

              <div style={{ marginTop:10 }}>
                <Btn onClick={download} variant="success">
                  ⬇ Download compressed PDF
                </Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ──────────────────────────────────────────────────────── */
const TABS = [
  { id:'image', icon:'🖼️', label:'Image Converter' },
  { id:'merge', icon:'📑', label:'PDF Merger'      },
  { id:'minify',icon:'🗜️', label:'PDF Compressor'  },
];
const fileToolsSchema = {
  "@context":    "https://schema.org",
  "@type":       "WebApplication",
  "name":        "Free File Tools — Tylented",
  "url":         "https://arhat.info/tools/files",
  "description": "Free online file tools: image format converter (PNG, JPG, WebP, BMP, GIF), image compressor, image resizer, PDF merger, PDF compressor, QR code generator. Files are processed in your browser — nothing is uploaded to a server.",
  "applicationCategory": "UtilitiesApplication",
  "operatingSystem": "Any",
  "isAccessibleForFree": true,
  "offers": { "@type":"Offer", "price":"0", "priceCurrency":"USD" },
  "featureList": [
    "Image Format Converter — PNG, JPG, WebP, BMP, GIF",
    "Image Compressor — reduce file size without quality loss",
    "Image Resizer — resize to exact dimensions",
    "PDF Merger — combine multiple PDFs into one",
    "PDF Compressor — reduce PDF file size (low, medium, high quality)",
    "QR Code Generator — URL, text, contact info"
  ],
  "creator": { "@type":"Organization","name":"Tylented","url":"https://arhat.info" }
};
 
const fileToolsFAQSchema = {
  "@context": "https://schema.org",
  "@type":    "FAQPage",
  "mainEntity": [
    {
      "@type":          "Question",
      "name":           "How do I convert PNG to JPG online for free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Use the free Image Converter at arhat.info/tools/files. Upload your PNG, select JPG as the output format, and download the converted file. The conversion happens in your browser — your image is never uploaded to any server."
      }
    },
    {
      "@type":          "Question",
      "name":           "How do I merge multiple PDF files for free without signing up?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Use the free PDF Merger at arhat.info/tools/files. Upload your PDF files, arrange the order by dragging, and click Download to get the combined PDF. No account required, no watermarks added, no file size restrictions for most documents."
      }
    },
    {
      "@type":          "Question",
      "name":           "How do I reduce a PDF file size for free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Use the PDF Compressor at arhat.info/tools/files. Upload your PDF and choose a compression level — low (minimal quality loss), medium (balanced), or high (maximum compression). The tool works best on PDFs that contain images. Download the compressed file instantly."
      }
    }
  ]
};

const Mapping = {
  'image-convert': 'image',
  'pdf-merge':     'merge',
  'pdf-compress':  'minify',
  'files':         'image',
  'file-tools':         'image',
};

const FILE_FAQS = {
  image: [
    {
      q: 'Does my image get uploaded to a server?',
      a: 'No — all image conversion, compression, and resizing happens entirely in your browser using the Canvas API. Your image never leaves your device. This makes it safe to use with private or sensitive photos.',
    },
    {
      q: 'What image formats can I convert between?',
      a: 'You can convert between PNG, JPG/JPEG, WebP, BMP, and GIF. WebP is recommended for web use — it produces smaller file sizes than PNG and JPG at equivalent quality. PNG is best for images that need transparency.',
    },
    {
      q: 'Why does converting PNG to JPG lose the transparent background?',
      a: 'JPG does not support transparency. When you convert a PNG with a transparent background to JPG, the transparency is filled with a solid color (usually white or black). Use WebP or PNG if you need to preserve transparency.',
    },
    {
      q: 'What quality setting should I use for image compression?',
      a: 'For web images, 75–85% quality is a good balance between file size and visual quality. Below 60% you may see noticeable compression artifacts. For print or archival use, stay above 90%. For thumbnails and previews, 60–70% is fine.',
    },
    {
      q: 'What is WebP and why should I use it?',
      a: 'WebP is a modern image format developed by Google. It produces files 25–35% smaller than JPG and 26% smaller than PNG at equivalent visual quality. All modern browsers support WebP. Use it for web images to improve page load speed.',
    },
    {
      q: 'What does resizing an image by percentage do?',
      a: 'It scales the image proportionally by the given percentage. 50% makes the image half its original dimensions in both width and height, resulting in roughly 25% of the original file size (since area = width × height).',
    },
  ],

  merge: [
    {
      q: 'Are my PDF files uploaded to a server?',
      a: 'Yes — PDF merging requires server-side processing because browsers cannot natively write PDF files. Your files are sent to the server, merged, and the result is returned. We do not store, log, or retain any uploaded files after processing.',
    },
    {
      q: 'Is there a file size or page limit for PDF merging?',
      a: 'There is no hard page limit. Very large PDFs (hundreds of MB) may time out depending on server resources. For best results, keep individual files under 50MB. If you have very large PDFs, try compressing them first.',
    },
    {
      q: 'Can I reorder PDFs before merging?',
      a: 'The PDFs are merged in the order you select or upload them. To control the order, upload them one at a time in the sequence you want, or name your files so they sort in the correct order before selecting them all.',
    },
    {
      q: 'What PDF versions are supported?',
      a: 'The merger supports standard PDF 1.4 through PDF 2.0. Password-protected PDFs cannot be merged — remove the password protection first. Scanned PDFs (image-only) merge fine but the text remains unsearchable unless OCR is applied separately.',
    },
    {
      q: 'Why is my merged PDF larger than the sum of the originals?',
      a: 'PDFs can contain embedded fonts, images, and resources. When merged, some resources may be duplicated if not de-duplicated by the merge tool. Images embedded in PDFs are also not re-compressed during merging.',
    },
    {
      q: 'Can I split a PDF instead of merging?',
      a: 'This tool currently only merges PDFs. For splitting, you can use Adobe Acrobat, PDF24, or Smallpdf. A PDF splitter tool is on our roadmap.',
    },
  ],

  minify: [
    {
      q: 'How does PDF compression work?',
      a: 'PDF compression reduces file size by re-compressing embedded images at lower quality, removing redundant metadata, and optimising the internal PDF structure. The three compression levels (low, medium, high) control how aggressively images are re-encoded.',
    },
    {
      q: 'Are my PDFs uploaded to a server for compression?',
      a: 'Yes — PDF compression requires server-side processing. Your file is sent to the server, compressed, and returned to you. We do not store, log, or retain uploaded files after the compressed version is returned.',
    },
    {
      q: 'What compression level should I choose?',
      a: 'Low compression: minimal quality loss, moderate size reduction — good for documents that need to look professional. Medium compression: balanced — good for email attachments. High compression: maximum size reduction with visible image quality loss — use for archiving or when file size is critical.',
    },
    {
      q: 'Why didn\'t my PDF get smaller after compression?',
      a: 'Some PDFs are already well-optimised, contain mostly text (which is already small), or have images encoded in a way that doesn\'t compress further. Text-heavy PDFs with no embedded images may show little to no reduction.',
    },
    {
      q: 'Will compression affect text readability?',
      a: 'No — text in PDFs is stored as vector data and is not affected by image compression settings. Only embedded images (photos, diagrams, scanned pages) are re-compressed. Text remains crisp at all compression levels.',
    },
    {
      q: 'Can I compress a scanned PDF?',
      a: 'Yes — scanned PDFs are essentially images inside a PDF wrapper, so they compress well. High compression on a scanned PDF will reduce file size significantly but may make fine print harder to read. Use medium compression for scanned documents you still need to read clearly.',
    },
    {
      q: 'What is the maximum file size I can compress?',
      a: 'There is no hard limit set on file size but very large files (over 100MB) may time out. For large scanned documents, try splitting the PDF first, compressing each part, then merging the compressed parts.',
    },
  ],

  qr: [
    {
      q: 'What can I encode in a QR code?',
      a: 'QR codes can encode URLs, plain text, email addresses (mailto:), phone numbers (tel:), SMS (smsto:), WiFi credentials, vCards (contact info), and geographic coordinates. URLs are the most common use case.',
    },
    {
      q: 'Does QR code generation happen in my browser?',
      a: 'Yes — QR codes are generated entirely in your browser using JavaScript. No data is sent to any server.',
    },
    {
      q: 'What is the maximum amount of data a QR code can hold?',
      a: 'QR codes can hold up to 7,089 numeric characters, 4,296 alphanumeric characters, or 2,953 bytes of binary data. In practice, keep URLs short for better scanning reliability — long data makes the QR code denser and harder to scan.',
    },
    {
      q: 'What QR code error correction level should I use?',
      a: 'L (7%): smallest QR code, use when it\'s displayed cleanly on screen. M (15%): good balance for most uses. Q (25%): use when the QR might get dirty or partially obscured. H (30%): maximum correction, use on printed materials or outdoor signage.',
    },
    {
      q: 'Why does my QR code fail to scan?',
      a: 'Common causes: insufficient contrast between the QR code and background, QR code too small (minimum 2cm × 2cm for reliable scanning), the encoded URL is too long making the code too dense, or printing quality issues. Always test your QR code before deploying it.',
    },
    {
      q: 'Can I customise the appearance of a QR code?',
      a: 'Basic customisation like foreground/background color is possible. Avoid low-contrast color combinations (dark on dark, light on light). Adding a logo to the center works if you use high error correction (Q or H level) since the logo covers part of the code.',
    },
  ],
};

const GENERIC_FILE_FAQS = [
  {
    q: 'Are these file tools free?',
    a: 'Yes — all file tools are completely free with no account required and no usage limits.',
  },
  {
    q: 'Are my files stored after processing?',
    a: 'No. Image tools run in your browser — nothing is uploaded. PDF tools process your file on the server and return the result immediately. Files are not stored or logged.',
  },
  {
    q: 'Can I use these tools on mobile?',
    a: 'Yes — all tools are mobile-responsive. Image tools work fully on mobile browsers. For PDF tools, tap the upload area to select files from your device.',
  },
];

function FileFAQSection({ active }) {
  const [open, setOpen] = useState(null);
  const faqs = FILE_FAQS[active] || GENERIC_FILE_FAQS;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {faqs.map((faq, i) => {
        const isOpen = open === i;
        return (
          <div key={i} style={{
            border:     `1px solid ${isOpen ? T.cyan+'55' : T.border}`,
            borderRadius:10,
            background:  isOpen ? T.cyan+'08' : T.bg2,
            overflow:   'hidden',
            transition: 'all .2s',
          }}>
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              style={{
                width:'100%', display:'flex', justifyContent:'space-between',
                alignItems:'center', padding:'13px 16px',
                background:'transparent', border:'none',
                cursor:'pointer', textAlign:'left', gap:12,
              }}
            >
              <span style={{
                fontSize:13.5, fontWeight:600,
                color: isOpen ? T.cyan : T.white,
                lineHeight:1.4, transition:'color .15s',
              }}>
                {faq.q}
              </span>
              <span style={{
                flexShrink:0, width:20, height:20, borderRadius:'50%',
                border:`1px solid ${isOpen ? T.cyan : T.border}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:13, color: isOpen ? T.cyan : T.muted,
                transition:'all .2s',
                transform: isOpen ? 'rotate(45deg)' : 'none',
              }}>+</span>
            </button>

            {isOpen && (
              <div style={{
                padding:'12px 16px 14px',
                fontSize:13, color:'rgba(220,230,250,.65)',
                lineHeight:1.75,
                borderTop:`1px solid ${T.cyan}20`,
              }}>
                {faq.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function FileTools() {
      const navigate = useNavigate();
    
      const hostname = window.location.hostname;
    
useEffect(() => {
  // Pass the two schemas for this page
  const schemas = [fileToolsSchema, fileToolsFAQSchema];
  const ids     = [];
  schemas.forEach((schema, i) => {
    const id  = `ld-json-tool-${i}`;
    document.getElementById(id)?.remove();
    const s   = document.createElement('script');
    s.id      = id;
    s.type    = 'application/ld+json';
    s.text    = JSON.stringify(schema);
    document.head.appendChild(s);
    ids.push(id);
  });
  return () => ids.forEach(id => document.getElementById(id)?.remove());
}, []);

    // app.dev.example.com => ["app", "dev", "example", "com"]
    const parts = hostname.split(".");
    
    const firstSubdomain =
      parts.length > 2 ? parts[0] : null;
    
    console.log(firstSubdomain);
    
    const path = window.location.pathname;
    
    // "/tools/useragent/"
    const match = path.match(/^\/tools\/files\/([^/]+)/);
    
    const toolName = match ? match[1] : null;
    
    console.log(toolName);
    
        const [active, setActive] = useState(toolName?toolName:firstSubdomain && firstSubdomain in Mapping ? Mapping[firstSubdomain] : 'image');
      

  return (
    <>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        * { box-sizing:border-box; margin:0; padding:0; }
        input[type=range] { height:4px; cursor:pointer; }
        ::placeholder { color:rgba(255,255,255,0.50) !important; }
        @media (max-width: 640px) {
  /* Image converter two-col → single col */
  .ft-two-col {
    grid-template-columns: 1fr !important;
  }
  /* PDF merger grid */
  .ft-pdf-grid {
    grid-template-columns: 1fr !important;
  }
  /* Tab labels shorten */
  .ft-tab-label {
    display: none;
  }
  /* Drop zone smaller on mobile */
  .ft-dropzone {
    padding: 18px 14px !important;
  }
}
      `}</style>
 <div className="page">
<SEO
  title="Image Converter, PDF Merger, PDF Compressor & File Tools — Free | Tylented"
  description="Free file tools: convert PNG to JPG or WebP, compress images, resize images, merge multiple PDF files into one, compress PDF to reduce file size, generate QR codes. Fast and private — your files stay in your browser. No sign-up, no upload limits."
  keywords="image converter online free, png to jpg free, jpg to webp converter, convert png to webp, image compressor online, resize image online free, merge pdf files online free, combine pdf free no sign up, compress pdf free, reduce pdf file size, pdf compressor online, qr code generator free, file converter browser"
  canonical="https://arhat.info/tools/files"
  og={{
    title:       "Free Image Converter, PDF Merger & File Tools — Tylented",
    description: "6 free file tools: PNG to JPG, image compressor, image resizer, merge PDFs, compress PDFs, QR code generator. Files stay in your browser. No sign-up.",
    url:         "https://arhat.info/tools/files",
    image:       "https://arhat.info/og-tools-files.png",
  }}
/>
    <section className="section" style={{ paddingTop: 0 }}>
      <div style={{
        minHeight:'100vh',
        background:T.bg,
        padding:'28px 0 48px',
        fontFamily:T.sans,
      }}>

        {/* Header */}
        <div style={{ marginBottom:20 }}>
          <h1 style={{
            fontSize:'clamp(1.5rem,3vw,2rem)',
            fontWeight:800, color:T.white,
            letterSpacing:'-.025em', marginBottom:5,
            fontFamily:T.sans,
          }}>
            🗂️ File Tools
          </h1>
          <p style={{ fontSize:13, color:T.muted }}>
            Image conversion runs entirely in your browser · PDF tools processed securely on the server. And, no we don't store your files.
          </p>
        </div>

        {/* Tabs */}
        <TabBar tabs={TABS} active={active} onChange={(id) => {
          navigate(`/tools/files/${id}/`)
            setActive(id);
          }}/>

        {/* Tool panel */}
       {/* Tool panel */}
<div style={{
  background:T.bg2,
  border:`1px solid ${T.border}`,
  borderTop:'none',
  borderRadius:'0 0 12px 12px',
  padding:'24px 22px',
}}>
  {active === 'image'  && <ImageConverter/>}
  {active === 'merge'  && <PDFMerger/>}
  {active === 'minify' && <PDFMinifier/>}
</div>

{/* FAQ */}
<div style={{ marginTop:48 }}>
  <h2 style={{
    fontSize:18, fontWeight:800, color:T.white,
    letterSpacing:'-.02em', marginBottom:6,
  }}>
    Frequently Asked Questions
  </h2>
  <p style={{ fontSize:13, color:T.muted, marginBottom:24 }}>
    Common questions about image conversion, PDF tools, and file utilities.
  </p>
  <FileFAQSection active={active}/>
</div>
      </div>
      </section></div>
    </>
  );
}
